/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataType } from '@grapecity/wijmo';
import { BoxItem } from 'models/setup/mobile/BoxLocation';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import GridContainer from 'components/grids/GridContainer';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';

interface BoxItemGridProps {
  items: BoxItem[];
  boxName?: string; // 선택된 박스명 (표시용)
  onItemSelect?: (item: BoxItem) => void; // 품목 선택 콜백
}

const BoxItemGrid = ({ items, boxName, onItemSelect }: BoxItemGridProps) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();

  // 최신 onItemSelect 콜백 참조 유지 (클로저 문제 해결)
  const onItemSelectRef = useRef(onItemSelect);
  useEffect(() => {
    onItemSelectRef.current = onItemSelect;
  }, [onItemSelect]);

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      width: 40,
      isReadOnly: true,
      cellTemplate: (grid: any) => grid.row._idx + 1,
    },
    {
      header: String(t('setup.label.품목명', '품목명')),
      binding: 'itemNm',
      width: 200,
      align: 'left',
    },
    {
      header: String(t('setup.label.품목수량', '품목수량')),
      binding: 'pakItemQty',
      width: 120,
      align: 'left',
    },
    {
      header: String(t('M-CODE', 'M-CODE')),
      binding: 'tgtEqpId',
      width: 150,
      align: 'left',
    },
    {
      header: String(t('setup.label.대상설비명', '대상설비명')),
      binding: 'tgtEqpNm',
      width: 200,
      align: 'left',
    },
    {
      header: String(t('setup.label.박스명', '박스명')),
      binding: 'pakBoxNm',
      width: '*',
      align: 'left',
      isReadOnly: true,
      visible: false,
    },
    {
      binding: 'itemStatCdNm',
      header: String(t('com.label.품목상태', '품목상태')),
      width: 120,
      isReadOnly: true,
    },
    {
      header: String(t('setup.label.설명', '설명')),
      binding: 'rmk',
      width: '*',
      align: 'left',
      visible: false,
    },
  ];

  const onInitialized = (grid: any) => {
    setFlexRef(grid);
    gridRef.current = grid;

    // row 클릭 시 품목 선택 콜백 호출 (ref를 통해 최신 콜백 참조)
    grid.hostElement.addEventListener('click', (e: MouseEvent) => {
      if (grid.rows.length === 0) return;
      const ht = grid.hitTest(e);
      if (ht.row < 0) return;

      const selectedItem = grid.rows[ht.row].dataItem as BoxItem;
      if (onItemSelectRef.current && selectedItem) {
        onItemSelectRef.current(selectedItem);
      }
    });
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.품목 목록', '품목 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{items?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
      </SubTitleLayout>
      <GridContainer
        layoutDefinition={layoutDefinition}
        rowData={items}
        deferResizing={false}
        initialized={onInitialized}
        excludePin={['no']}
        excludeFilter={['no']}
        isSelector={false}
        align={'center'}
      />
    </>
  );
};

export default BoxItemGrid;
